import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

const MAX_CORSISTI = 30;
/** Righe verticali da riservare nella tabella: intestazione + fino a 30 corsisti */
const TABLE_ROWS_LAYOUT = 31;

function formatTelefono(u) {
  const parts = [u.telefono1, u.telefono2].filter(Boolean);
  return parts.length ? parts.join(" / ") : "—";
}

function nominativoUtente(u) {
  const c = (u.cognome || "").trim();
  const n = (u.nome || "").trim();
  const s = `${c} ${n}`.trim();
  return s || "—";
}

/**
 * PDF lista corsisti per sessione d'esame di gruppo: una pagina A4 verticale,
 * massimo 30 corsisti. Testata su più righe (nessuna sovrapposizione).
 */
export function generateSessioneGruppoPdf({
  corsoDenominazione,
  dataLabel,
  orarioLabel,
  utenti,
  idCalendarioEsame,
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 7;
  const textMaxW = pageW - margin * 2;

  const list = Array.isArray(utenti) ? [...utenti] : [];
  list.sort((a, b) => {
    const ca = (a.cognome || "").localeCompare(b.cognome || "", "it", { sensitivity: "base" });
    if (ca !== 0) return ca;
    return (a.nome || "").localeCompare(b.nome || "", "it", { sensitivity: "base" });
  });

  const totalIscritti = list.length;
  const truncated = totalIscritti > MAX_CORSISTI;
  const listLimited = list.slice(0, MAX_CORSISTI);

  const body = listLimited.map((u) => [
    nominativoUtente(u),
    formatTelefono(u),
    (u.denominazione_agenzia && String(u.denominazione_agenzia).trim()) || "—",
    "",
  ]);

  if (body.length === 0) {
    body.push(["—", "—", "—", ""]);
  }

  // --- Testata: caratteri più grandi per leggibilità in stampa ---
  const titleLineH = 7;
  const corsoFontSize = 10;
  const metaFontSize = 10;
  const truncFontSize = 8.5;
  const lineHCorso = 4.7;
  const lineHMeta = 4.9;
  const lineHTrunc = 4.3;

  doc.setFontSize(corsoFontSize);
  doc.setFont("helvetica", "normal");
  const corsoFull = `Corso: ${corsoDenominazione || "—"}`;
  const corsoLines = doc.splitTextToSize(corsoFull, textMaxW);

  const metaLine = `Data: ${dataLabel || "—"}   |   Orario: ${orarioLabel || "—"}`;
  const truncLine = truncated
    ? `Elenco limitato ai primi ${MAX_CORSISTI} corsisti su ${totalIscritti} (ordine per cognome).`
    : null;

  let y = margin + 4;
  y += titleLineH;
  y += corsoLines.length * lineHCorso;
  y += lineHMeta;
  if (truncLine) y += lineHTrunc;
  const tableStartY = y + 3;

  const headerBgH = tableStartY - 1;
  doc.setFillColor(240, 241, 244);
  doc.rect(0, 0, pageW, headerBgH, "F");

  y = margin + 4;
  doc.setTextColor(33, 37, 41);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Sessione di gruppo — elenco corsisti", margin, y);
  y += titleLineH;

  doc.setFontSize(corsoFontSize);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(45, 45, 45);
  corsoLines.forEach((ln) => {
    doc.text(ln, margin, y);
    y += lineHCorso;
  });

  doc.text(metaLine, margin, y);
  y += lineHMeta;

  if (truncLine) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(truncFontSize);
    doc.setTextColor(160, 70, 0);
    doc.text(truncLine, margin, y);
  }

  const tableWidth = pageW - margin * 2;
  const bottomMargin = margin;
  const roomForTable = pageH - tableStartY - bottomMargin;

  // Sempre dimensionare le righe come se ci fossero 30 corsisti (+ header tabella),
  // altrimenti con pochi iscritti le righe diventano enormi e si riempie la pagina prima.
  const rawRowH = (roomForTable - 1) / TABLE_ROWS_LAYOUT;
  const rowHeight = Math.max(4.3, Math.min(7.8, rawRowH));
  const tableFontSize =
    rowHeight >= 6.6 ? 9 : rowHeight >= 5.5 ? 8.5 : rowHeight >= 4.7 ? 8 : 7.5;

  autoTable(doc, {
    startY: tableStartY,
    head: [["Nominativo", "Tel.", "Agenzia", "Note"]],
    body,
    theme: "grid",
    tableLineWidth: 0.1,
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    showFoot: "never",
    styles: {
      font: "helvetica",
      fontSize: tableFontSize,
      cellPadding: { top: 0.55, right: 1, bottom: 0.55, left: 1 },
      minCellHeight: rowHeight,
      lineColor: [165, 165, 165],
      lineWidth: 0.1,
      valign: "middle",
      overflow: "ellipsize",
    },
    headStyles: {
      fillColor: [52, 58, 64],
      textColor: 255,
      fontStyle: "bold",
      fontSize: tableFontSize,
      cellPadding: { top: 0.6, right: 1, bottom: 0.6, left: 1 },
      minCellHeight: rowHeight,
      halign: "left",
    },
    columnStyles: {
      0: { cellWidth: tableWidth * 0.22, halign: "left" },
      1: { cellWidth: tableWidth * 0.12, halign: "left" },
      2: { cellWidth: tableWidth * 0.16, halign: "left" },
      3: { cellWidth: tableWidth * 0.5, halign: "left" },
    },
    margin: { left: margin, right: margin, bottom: bottomMargin },
  });

  const fileName =
    idCalendarioEsame != null && idCalendarioEsame !== ""
      ? `Sessione_gruppo_esame_${idCalendarioEsame}.pdf`
      : "Sessione_gruppo_esame.pdf";
  doc.save(fileName);
}
